import { useCallback, useEffect, useRef, useState } from 'react';
import { QR_MISS_FADE_MS } from '../components/scanner/qrScanMessages';

/**
 * Single status line shared by pages that scan: the copy renders either in the
 * page's reserved slot (`.pbl-page-header-status`) or, while the camera sheet is
 * open, as an overlay on the preview (`.order-detail-message`).
 */
export const MESSAGE_TONES = {
  INFO: 'info',
  SUCCESS: 'success',
  /** Valid input, nothing to show for it (e.g. a SKU with no locations). */
  WARNING: 'warning',
  ERROR: 'error',
} as const;
export type MessageTone = typeof MESSAGE_TONES[keyof typeof MESSAGE_TONES];

/** Orange warnings hold at full opacity this long before the fade starts. */
export const WARNING_DISPLAY_MS = 5000;
/** Length of that fade. Keep in sync with the `pbl-status-warning-fade` animation. */
export const WARNING_FADE_MS = 2000;
/** Warnings clear as the fade lands, so a repeat gets a fresh element to animate. */
const WARNING_AUTO_CLEAR_MS = WARNING_DISPLAY_MS + WARNING_FADE_MS;

export interface ShowMessageOptions {
  /** Substring of the message highlighted as the offending scan/entry. */
  readonly invalidInput?: string | null;
  /** Clear the message automatically after this many ms. */
  readonly autoClearMs?: number;
  /** Fade the camera overlay (QR decode/parse miss). Also auto-clears at QR_MISS_FADE_MS. */
  readonly fade?: boolean;
}

export interface StatusMessageState {
  readonly message: string;
  readonly tone: MessageTone;
  readonly invalidInput: string | null;
  /** Bumped on every message so the slot re-runs its entry/fade animation on repeats. */
  readonly flashKey: number;
  /** True when this message should use the QR-miss fade class on the camera overlay. */
  readonly fades: boolean;
  /** Single entry point so tone, highlight, and auto-clear stay in sync. */
  showMessage(text: string, tone?: MessageTone, options?: ShowMessageOptions): void;
  clearMessage(): void;
}

/** Reserved page slot classes; render inside `.pbl-page-header-status`. */
export function statusSlotMessageClass(tone: MessageTone): string {
  if (tone === MESSAGE_TONES.SUCCESS) return 'pbl-message pbl-message-success';
  if (tone === MESSAGE_TONES.WARNING) return 'pbl-message pbl-message-warning-banner';
  if (tone === MESSAGE_TONES.ERROR) return 'pbl-message pbl-message-error-banner';
  return 'pbl-message';
}

/** Camera preview overlay classes, shared with the order detail scan bar. */
export function cameraMessageClass(tone: MessageTone, fades = false): string {
  if (tone === MESSAGE_TONES.SUCCESS) return 'order-detail-message order-detail-message-success';
  if (tone === MESSAGE_TONES.WARNING) return 'order-detail-message order-detail-message-warning';
  if (tone === MESSAGE_TONES.ERROR) {
    return fades
      ? 'order-detail-message order-detail-message-error webcam-qr-miss-fade'
      : 'order-detail-message order-detail-message-error';
  }
  return 'order-detail-message order-detail-message-default';
}

export function useStatusMessage(): StatusMessageState {
  const [message, setMessage] = useState('');
  const [tone, setTone] = useState<MessageTone>(MESSAGE_TONES.INFO);
  const [invalidInput, setInvalidInput] = useState<string | null>(null);
  const [flashKey, setFlashKey] = useState(0);
  const [fades, setFades] = useState(false);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingClear = useCallback((): void => {
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
  }, []);

  const clearMessage = useCallback((): void => {
    cancelPendingClear();
    setMessage('');
    setTone(MESSAGE_TONES.INFO);
    setInvalidInput(null);
    setFades(false);
  }, [cancelPendingClear]);

  const showMessage = useCallback((
    text: string,
    nextTone: MessageTone = MESSAGE_TONES.INFO,
    options?: ShowMessageOptions,
  ): void => {
    cancelPendingClear();
    const shouldFade = options?.fade === true;
    setMessage(text);
    setTone(nextTone);
    setInvalidInput(options?.invalidInput ?? null);
    setFades(shouldFade);
    setFlashKey((key) => key + 1);
    const autoClearMs = options?.autoClearMs
      ?? (shouldFade
        ? QR_MISS_FADE_MS
        : nextTone === MESSAGE_TONES.WARNING
          ? WARNING_AUTO_CLEAR_MS
          : undefined);
    if (autoClearMs != null) {
      clearTimerRef.current = setTimeout(() => {
        clearTimerRef.current = null;
        setMessage('');
        setTone(MESSAGE_TONES.INFO);
        setInvalidInput(null);
        setFades(false);
      }, autoClearMs);
    }
  }, [cancelPendingClear]);

  useEffect(() => cancelPendingClear, [cancelPendingClear]);

  return { message, tone, invalidInput, flashKey, fades, showMessage, clearMessage };
}
